import * as React from "react";
import { useCallback } from "react";

import BoxContainer from "@hl/base-components/lib/BoxContainer";
import {
  DESTRUCTIVE_COLOR,
  SECONDARY_COLOR,
} from "@hl/base-components/lib/theme/button";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { useModalStack } from "@hl/shared-features/lib/features/modal";
import { Button, Group, Stack, Text } from "@mantine/core";
import { usePrivy, useLinkAccount } from "@privy-io/react-auth";

import {
  PublicUserProfileQuery,
  useLinkEmailMutation,
  useUnlinkEmailMutation,
} from "../queries/profile.graphql.generated";

export interface Props {
  profile?: PublicUserProfileQuery["getPublicAccountSettings"];
  loading: boolean;
}

export const LinkEmailInput = ({ loading }: Props) => {
  const { unlinkEmail, user } = usePrivy();
  const { pushModal, popModal } = useModalStack();

  const [linkEmailMutation, { loading: loadingLinkEmail }] =
    useLinkEmailMutation();

  const [unlinkEmailMutation, { loading: loadingUnlinkEmail }] =
    useUnlinkEmailMutation();

  const { linkEmail } = useLinkAccount({
    onSuccess: ({ user, linkMethod }) => {
      if (user?.email?.address && linkMethod === "email") {
        linkEmailMutation();
      }
    },
  });

  const unlinkEmailOnClickHandler = useCallback(
    async (email: string) => {
      await unlinkEmail(email);
      await unlinkEmailMutation();
    },
    [unlinkEmail, unlinkEmailMutation]
  );

  return (
    <Stack spacing={4}>
      <BoxContainer p="8px 16px">
        <Group position="apart">
          <Group spacing={10}>
            <Text
              size="sm"
              color={user?.email ? TEXT_COLOR.PRIMARY : TEXT_COLOR.PLACEHOLDER}
            >
              {user?.email ? user.email.address : "example@example.com"}
            </Text>
          </Group>
          <Button
            component="a"
            target="_blank"
            onClick={
              user?.email?.address
                ? () =>
                    pushModal(
                      <UnlinkEmailModal
                        loading={loadingUnlinkEmail}
                        close={popModal}
                        unlinkEmail={() => {
                          unlinkEmailOnClickHandler(user?.email?.address || "");
                          popModal();
                        }}
                      />,
                      {
                        title: (
                          <Text size="md" fw={WEIGHT_BOLD}>
                            Unlink email
                          </Text>
                        ),
                      }
                    )
                : linkEmail
            }
            color={SECONDARY_COLOR}
            size="xs"
            px={12}
            loading={loading || loadingLinkEmail}
          >
            {user?.email ? "Unlink Email" : "Link Email"}
          </Button>
        </Group>
      </BoxContainer>
    </Stack>
  );
};

const UnlinkEmailModal = ({
  close,
  unlinkEmail,
  loading,
}: {
  close: () => void;
  unlinkEmail: () => void;
  loading: boolean;
}) => {
  return (
    <Stack>
      <Text>Are you sure you want to unlink your email from your account?</Text>
      <Group noWrap>
        <Button size="xl" w="100%" color={SECONDARY_COLOR} onClick={close}>
          Cancel
        </Button>
        <Button
          size="xl"
          w="100%"
          color={DESTRUCTIVE_COLOR}
          onClick={unlinkEmail}
          loading={loading}
        >
          Unlink
        </Button>
      </Group>
    </Stack>
  );
};
